import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useToast } from 'hooks'
import { useFetch } from 'hooks/useFetch'
import { useIntl } from 'react-intl'
import { ProxyTo } from '../../../../../pages/api/proxy/[...slug]'
import { messages } from '../CreationModal/messages'
import { Template, TemplateListResponse, TemplatePayload } from '../typings'
import { transformDates } from './useTemplateQuery'

type SaveParams = {
  data: TemplatePayload
  onSuccess?: () => void
  onError?: () => void
}

type RemoveParams = {
  id: string
  onSuccess?: () => void
  onError?: () => void
}

function updateTemplatesCache(
  queryClient: ReturnType<typeof useQueryClient>,
  template: Template
) {
  const { promotion, ...templateWithoutPromotion } = template

  queryClient.setQueryData<TemplateListResponse>(
    ['list-templates'],
    (oldData) => {
      if (!oldData) return undefined

      const newTemplate =
        templateWithoutPromotion as TemplateListResponse[number]
      const existingIndex = oldData.findIndex((t) => t.id === template.id)

      if (existingIndex !== -1) {
        return oldData.map((t) => (t.id === template.id ? newTemplate : t))
      }

      return [...oldData, newTemplate]
    }
  )
}

function removeTemplateFromCache(
  queryClient: ReturnType<typeof useQueryClient>,
  templateId: string
) {
  queryClient.setQueryData<TemplateListResponse>(
    ['list-templates'],
    (oldData) => {
      if (!oldData) return undefined

      return oldData.filter((t) => t.id !== templateId)
    }
  )

  queryClient.removeQueries({ queryKey: ['template-by-id', templateId] })
}

export function useTemplateMutation() {
  const { fetcherWrapper } = useFetch()
  const queryClient = useQueryClient()
  const { errorToast, positiveToast } = useToast()
  const { $t } = useIntl()

  const saveMutation = (params: SaveParams) => {
    const { data } = params
    const isUpdate = !!data.id

    const url = new URL('/api/proxy/templates', window.location.origin)

    if (isUpdate) {
      url.pathname = `${url.pathname}/${data.id}`
    }

    return fetcherWrapper(url, {
      method: isUpdate ? 'PUT' : 'POST',
      headers: {
        'x-vtex-proxy-to': ProxyTo.PROMOTIONS_STORAGE,
      },
      body: JSON.stringify(data),
    }).then((r) => r.json()).then(transformDates)
  }

  const { mutate: save, isPending: isPendingSave } = useMutation({
    mutationFn: saveMutation,
    onSuccess: async (data, variables) => {
      queryClient.setQueryData(['template-by-id', data.id], data)
      updateTemplatesCache(queryClient, data)

      positiveToast($t(messages.saveTemplate.success))

      variables.onSuccess?.()
    },
    onError: (error, variables) => {
      errorToast($t(messages.saveTemplate.error))

      variables.onError?.()
    },
  })

  const deleteMutation = (params: RemoveParams) => {
    const { id } = params

    const url = new URL(`/api/proxy/templates/${id}`, window.location.origin)

    return fetcherWrapper(url, {
      method: 'DELETE',
      headers: {
        'x-vtex-proxy-to': ProxyTo.PROMOTIONS_STORAGE,
      },
    })
  }

  const { mutate: remove, isPending: isPendingDelete } = useMutation({
    mutationFn: deleteMutation,
    onSuccess: async (_, variables) => {
      removeTemplateFromCache(queryClient, variables.id)

      positiveToast($t(messages.deleteTemplate.success))

      variables.onSuccess?.()
    },
    onError: (error, variables) => {
      errorToast($t(messages.deleteTemplate.error))

      variables.onError?.()
    },
  })

  return {
    save,
    remove,
    isLoading: isPendingSave || isPendingDelete,
  }
}
